import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Voicemail,
  MessageSquare,
  Mail,
  Clock,
  User,
} from "lucide-react";
import { formatPhoneNumber } from "../../utils/formatPhone";

interface CallTimelineCardProps {
  callLog: any;
  onCallBack?: () => void;
  onTextBack?: () => void;
  onEmailBack?: () => void;
  onViewDetails?: () => void;
}

function formatCallDuration(secs: number): string {
  if (!secs || secs < 1) return "0s";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTime(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function getContactName(callLog: any): string {
  if (callLog.direction === "outbound") {
    return callLog.to?.displayName || formatPhoneNumber(callLog.to?.phoneE164) || "Unknown";
  }
  return callLog.from?.displayName || formatPhoneNumber(callLog.from?.phoneE164) || "Unknown";
}

function getCallerName(callLog: any): string | null {
  // For outbound calls, the "caller" is the firm user
  if (callLog.direction === "outbound") {
    return callLog.from?.displayName || null;
  }
  return null;
}

const CallTimelineCard: React.FC<CallTimelineCardProps> = ({
  callLog,
  onCallBack,
  onTextBack,
  onEmailBack,
  onViewDetails,
}) => {
  const status = callLog.status;
  const contactName = getContactName(callLog);
  const callerName = getCallerName(callLog);
  const duration = callLog.durationSecs;

  const renderContent = () => {
    switch (status) {
      case "completed":
        return (
          <div className="w-full">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                {callLog.direction === "outbound" ? (
                  <PhoneOutgoing className="h-4 w-4 text-emerald-700" />
                ) : (
                  <PhoneIncoming className="h-4 w-4 text-emerald-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {callLog.direction === "outbound" ? "Outgoing call" : "Incoming call"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {callLog.direction === "outbound" ? `To ${contactName}` : `From ${contactName}`}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-medium text-emerald-700">
                  {formatCallDuration(duration)}
                </p>
                <p className="text-[10px] text-slate-400">
                  {formatTime(callLog.startedAt || callLog.createdAt)}
                </p>
              </div>
            </div>
            {callerName && (
              <div className="flex items-center gap-1.5 mt-1.5 ml-12">
                <User className="h-3 w-3 text-slate-400" />
                <span className="text-[11px] text-slate-500">{callerName}</span>
              </div>
            )}
          </div>
        );

      case "missed":
      case "no_answer":
        return (
          <div className="w-full">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <PhoneMissed className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-700 truncate">
                  Missed call
                </p>
                <p className="text-xs text-slate-500 truncate">
                  From {contactName}
                </p>
              </div>
              <p className="text-[10px] text-slate-400 flex-shrink-0">
                {formatTime(callLog.startedAt || callLog.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2.5 ml-12">
              {onCallBack && (
                <button
                  onClick={(e) => { e.stopPropagation(); onCallBack(); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  Call back
                </button>
              )}
              {onTextBack && (
                <button
                  onClick={(e) => { e.stopPropagation(); onTextBack(); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <MessageSquare className="h-3 w-3" />
                  Text
                </button>
              )}
              {onEmailBack && (
                <button
                  onClick={(e) => { e.stopPropagation(); onEmailBack(); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                >
                  <Mail className="h-3 w-3" />
                  Email
                </button>
              )}
            </div>
          </div>
        );

      case "voicemail":
        return (
          <div className="flex items-center gap-3 w-full">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Voicemail className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 truncate">
                Voicemail
              </p>
              <p className="text-xs text-slate-500 truncate">
                From {contactName}
                {callLog.voicemail?.duration ? ` \u00B7 ${formatCallDuration(callLog.voicemail.duration)}` : ""}
              </p>
            </div>
            <p className="text-[10px] text-slate-400 flex-shrink-0">
              {formatTime(callLog.startedAt || callLog.createdAt)}
            </p>
          </div>
        );

      case "failed":
        return (
          <div className="w-full">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <PhoneOff className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-700">Call failed</p>
                <p className="text-xs text-slate-500 truncate">
                  To {contactName}
                </p>
              </div>
              {onCallBack && (
                <button
                  onClick={(e) => { e.stopPropagation(); onCallBack(); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  Retry
                </button>
              )}
            </div>
          </div>
        );

      case "initiated":
      case "ringing":
      case "in_progress":
        return (
          <div className="flex items-center gap-3 w-full">
            <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Phone className="h-4 w-4 text-blue-600 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-700">
                {status === "in_progress" ? "On call" : "Calling..."}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {contactName}
              </p>
            </div>
            <Clock className="h-3.5 w-3.5 text-blue-400 animate-spin flex-shrink-0" />
          </div>
        );

      default:
        return (
          <div className="flex items-center gap-3 w-full">
            <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Phone className="h-4 w-4 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-700 truncate">
                {callLog.direction === "outbound" ? "Outgoing call" : "Incoming call"}
              </p>
              <p className="text-xs text-slate-500 truncate">{contactName}</p>
            </div>
            <p className="text-[10px] text-slate-400 flex-shrink-0">
              {formatTime(callLog.startedAt || callLog.createdAt)}
            </p>
          </div>
        );
    }
  };

  const borderColorMap: Record<string, string> = {
    completed: "border-l-emerald-500",
    missed: "border-l-red-500",
    no_answer: "border-l-red-500",
    voicemail: "border-l-amber-500",
    failed: "border-l-red-500",
    initiated: "border-l-blue-500",
    ringing: "border-l-blue-500",
    in_progress: "border-l-blue-500",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex justify-center my-3"
    >
      <div
        onClick={onViewDetails}
        className={`
          max-w-sm w-full rounded-xl border border-slate-200 border-l-4
          ${borderColorMap[status] || "border-l-slate-300"}
          bg-white shadow-sm px-4 py-3
          ${onViewDetails ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
        `}
      >
        {renderContent()}
      </div>
    </motion.div>
  );
};

export default CallTimelineCard;
